import React, { useRef, useMemo, useState } from 'react';
import CategoryNavigation from '../../components/CategoryNavigation/CategoryNavigation';
import CustomSection from '../../components/CustomSection/CustomSection';
import DetailedSection from '../../components/DetailedSection/DetailedSection';
import FreeTrialGatedBlock from '../../components/GatedBlock/FreeTrialGatedBlock';
import GatedBlock from '../../components/GatedBlock/GatedBlock';
import { useGlobalContext } from '../../components/GlobalContextProvider/GlobalContextProvider';
import Loader from '../../components/Loader/Loader';
import SectionNavigation from '../../components/SectionNavigation/SectionNavigation';
import {
  CUSTOM_SECTION_TYPE,
  RM_CONTRACTORS_TYPE,
  MANUFACTURE_CONTRACTORS_TYPE,
  STYLED_BLOCK_TYPE,
  EQUIPMENT_TYPES_TYPE,
  IMAGE_MATRIX_TYPE,
  TABLE_TYPE,
  SUBSECTION_TYPE,
  IMAGE_TYPE,
  PARAGRAPH_TYPE,
  SECTION_TYPE,
} from '../../consts/content-types';
import {
  FREE_USER_LEVEL,
  PAID_RESTRICTION_TYPE,
  TRIAL_RESTRICTION_TYPE,
  TRIAL_USER_LEVEL,
} from '../../consts/restrictionTypes';
import useBreadcrumbsNav from '../../hooks/useBreadcrumbsNav';
import useDynamicContentFetch from '../../hooks/useDynamicContentFetch';
import useSendIsContentCroppedToGTM from '../../hooks/useSendIsContentCroppedToGTM';
import {
  DetailedPageNav,
  Navigation,
} from '../../shared-styled/blocks/detailedPageBlocks';
import { DetailedContentContainer } from '../../shared-styled/layout/DetailedContentContainer';
import { DetailPageTitle } from '../../shared-styled/typography/titles';
import styles from '../../styles/print-media.module.css';
import { camelToTitle } from '../../utils/caseconverters';
import { parseContent } from '../../utils/content';
import { IS_NEXT_EXPORT } from '../../utils/export';
import BasePageWrap from '../BasePage';
import {
  SectionTypeFIReport,
  SectionContainerFIReport,
} from './FIReportPage.styled';

const CUSTOM_TYPES = [CUSTOM_SECTION_TYPE, STYLED_BLOCK_TYPE, SECTION_TYPE];

const FIReportPage = (props) => {
  const { percentOfCoveredTitle, isLogged, user } = useGlobalContext();
  const [isLoading, setIsLoading] = useState();

  const pageData = useDynamicContentFetch(props, setIsLoading);
  const { contents } = useMemo(
    () => parseContent(pageData.content),
    [pageData.content],
  );
  const titleRef = useRef();
  useBreadcrumbsNav(titleRef);

  const isDetailedNavActive = pageData?.relatedUrl?.length > 1;
  const freeUserRestriction =
    user &&
    user.userLevel === FREE_USER_LEVEL &&
    (pageData.restrictionType === TRIAL_RESTRICTION_TYPE ||
      pageData.restrictionType === PAID_RESTRICTION_TYPE);
  const trialUserRestriction =
    user &&
    user.userLevel === TRIAL_USER_LEVEL &&
    pageData.restrictionType === PAID_RESTRICTION_TYPE;

  const transformSectionValuesToArray = (obj) =>
    Object.entries(obj).map(([key, value]) => ({
      type: SUBSECTION_TYPE,
      value: {
        content: value,
        header: camelToTitle(key),
      },
    }));

  const transformContractorData = ({ obj, type } = {}) => {
    const { contractors: value, kind, note } = obj || {};
    // Keep table structure
    const TABLE_ENTRY = {
      type: TABLE_TYPE,
      value,
    };

    const SUB = 'sub';
    const PRIME = 'prime';

    const AIRFRAME = '0';
    const PROPULSION = '1';
    const ELECTRONICS = '2';
    const ARMAMENT = '3';

    const KEYS_MAPPING = {
      kind: {
        [MANUFACTURE_CONTRACTORS_TYPE]: {
          label: 'Contractor type',
          value: {
            [PRIME]: 'Prime',
            [SUB]: 'Subcontractors',
          },
        },
        [RM_CONTRACTORS_TYPE]: {
          label: 'Kind',
          value: {
            [AIRFRAME]: 'Airframe',
            [PROPULSION]: 'Propulsion',
            [ELECTRONICS]: 'Electronics',
            [ARMAMENT]: 'Armament',
          },
        },
      },
    };

    const { value: kindValues = {} } = KEYS_MAPPING.kind?.[type] || {};
    const { [kind]: kindContent } = kindValues || {};

    // Add subsection objects for kind and note
    const extraSections = [
      kind !== undefined && {
        type: SUBSECTION_TYPE,
        value: { header: kindContent },
      },
      note !== undefined && {
        type: SUBSECTION_TYPE,
        value: { content: note, header: 'Contractors notes' },
      },
    ].filter(Boolean); // Remove `undefined` entries

    return [TABLE_ENTRY, ...extraSections];
  };

  const transformImageMatrixValuesToArray = (obj) =>
    Object.entries(obj).flatMap(([key, value]) => {
      if (typeof value === 'object' && value !== null) {
        const SUBKEY_MAPPING = {
          oppStr: 'Opportunities/Strengths',
          weaknesses: 'Risks/Weaknesses',
          description: '', // Special case: Exclude subkey from header
        };
        return Object.entries(value).map(([subKey, subValue]) => {
          const mappedSubKey = SUBKEY_MAPPING[subKey] ?? camelToTitle(subKey);
          const header =
            SUBKEY_MAPPING[subKey] === '' ? camelToTitle(key) : mappedSubKey;

          return {
            type: SUBSECTION_TYPE,
            value: {
              content: subValue,
              header,
            },
          };
        });
      }

      return transformSectionValuesToArray(value);
    });
  function transformSectionData(section, level = 0) {
    if (!section?.value?.body) return [];

    // Extract headers while keeping indentation levels
    const { header, subheader, body } = section.value;
    const sectionMeta = {
      id: section.id,
      type: section.type,
      header,
      subheader,
      level,
    };

    return [
      sectionMeta, // Keep the section itself with level
      ...body.flatMap((item) => {
        if (item.type === SUBSECTION_TYPE) {
          return transformSectionData(item, level + 2);
        }
        // eslint-disable-next-line no-use-before-define
        return { ...transformContentsEl(item), level: level + 1 };
      }),
    ];
  }

  function transformContentsEl(el) {
    if (!el?.value) return el;

    const { value, type } = el;
    const { header, style, ...rest } = value;

    switch (type) {
      case SECTION_TYPE:
        return transformSectionData(el);

      case IMAGE_TYPE:
      case PARAGRAPH_TYPE:
      case TABLE_TYPE:
        return { ...el, isShownSectionType: false };

      case EQUIPMENT_TYPES_TYPE:
        return { ...el, value: transformSectionValuesToArray(value) };

      case IMAGE_MATRIX_TYPE:
        return { ...el, value: transformImageMatrixValuesToArray(value) };

      case MANUFACTURE_CONTRACTORS_TYPE:
      case RM_CONTRACTORS_TYPE:
        return { ...el, value: transformContractorData({ obj: value, type }) };

      default:
        if (CUSTOM_TYPES.includes(type)) {
          return { ...el, value: { ...rest, header: header || style } };
        }
        return el;
    }
  }

  const transformContents = (data) => data.map((el) => transformContentsEl(el));

  const transformedContents = useMemo(
    () => transformContents(contents),
    [contents],
  );

  useSendIsContentCroppedToGTM(contents, pageData.restrictionType);
  return (
    <DetailedContentContainer>
      <Navigation
        $isDetailedNavActive={false}
        $opacity={percentOfCoveredTitle}
        $isVisibleOnPrint={true}
      >
        <CategoryNavigation className={styles.hideForPrint} />
        <DetailedPageNav ref={titleRef}>
          <DetailPageTitle>{pageData.title}</DetailPageTitle>
          {isDetailedNavActive && (
            <SectionNavigation
              urlItems={pageData.relatedUrl}
              isModifiedSectionNavigationOnPrint={true}
            />
          )}
        </DetailedPageNav>
      </Navigation>
      <div>
        {transformedContents?.map((el, index) =>
          Array.isArray(el) ? (
            <div key={`array-${index}`}>
              {el.map((nestedEl, nestedIndex) => (
                <div key={`${nestedEl.id}-${nestedIndex}`}>
                  {nestedEl.type === SECTION_TYPE && (
                    <SectionContainerFIReport $level={nestedEl.level}>
                      <SectionTypeFIReport>
                        {nestedEl.header}
                      </SectionTypeFIReport>
                      {nestedEl.subheader && <h3>{nestedEl.subheader}</h3>}
                    </SectionContainerFIReport>
                  )}

                  {nestedEl.type === SUBSECTION_TYPE && (
                    <SectionContainerFIReport $level={nestedEl.level}>
                      <SectionTypeFIReport>
                        {nestedEl.header}
                      </SectionTypeFIReport>
                    </SectionContainerFIReport>
                  )}

                  {CUSTOM_TYPES.includes(nestedEl.type) ? (
                    <CustomSection
                      index={nestedIndex}
                      el={nestedEl}
                      level={nestedEl.level}
                    />
                  ) : (
                    <DetailedSection
                      index={nestedIndex}
                      el={nestedEl}
                      isShownSectionType={nestedEl.isShownSectionType}
                      level={nestedEl.level}
                      isIndented={[
                        EQUIPMENT_TYPES_TYPE,
                        IMAGE_MATRIX_TYPE,
                      ].includes(nestedEl.type)}
                    />
                  )}
                </div>
              ))}
            </div>
          ) : CUSTOM_TYPES.includes(el.type) ? (
            <CustomSection key={`${el.id}-${index}`} index={index} el={el} />
          ) : (
            <DetailedSection
              key={`${el.id}-${index}`}
              index={index}
              el={el}
              isShownSectionType={el.isShownSectionType}
              isIndented={[EQUIPMENT_TYPES_TYPE, IMAGE_MATRIX_TYPE].includes(
                el.type,
              )}
            />
          ),
        )}
      </div>

      {isLoading && <Loader />}
      {!IS_NEXT_EXPORT && pageData.restricted && (
        <>
          {!isLogged && <GatedBlock isHigher={true} />}

          {isLogged &&
            process.env.NEXT_PUBLIC_LIMITED_FREE_TRIAL_ENABLED === 'True' &&
            (trialUserRestriction || freeUserRestriction) && (
              <FreeTrialGatedBlock
                isHigher={true}
                recordName={pageData.title}
              />
            )}
        </>
      )}
    </DetailedContentContainer>
  );
};

export default BasePageWrap(FIReportPage);
